import { c as _c } from "react/compiler-runtime";
import { Loading } from '@/components/Atoms/Loading/Loading';
import { analystReactionsPageFetcher, AnalystReactionsPageRenderer } from '@/components/Pages/AnalystReactions/AnalystReactionViewPage';
import { ForbiddenPage } from '@/components/Pages/Errors/ForbiddenPage';
import { NotFoundErrorPage } from '@/components/Pages/Errors/NotFoundErrorPage';
import { UnavailableForLegalReasonsPage } from '@/components/Pages/Errors/UnavailableForLegalReasonsPage';
import { insightPageFetcher, InsightPageRenderer } from '@/components/Pages/Insights/InsightExecSumPage';
import { newsRsfPageFetcher, NewsRsfRenderer } from '@/components/Pages/News/NewsRsfPage';
import { shortsPageFetcher, ShortsPageRenderer } from '@/components/Pages/Shorts/ShortsViewPage';
import { themePageFetcher, ThemePageRenderer } from '@/components/Pages/Themes/ThemePage';
import { useMounted } from '@/hooks/useMounted';
import type { IResultItem } from '@/interfaces';
import { ContentType } from '@/interfaces';
import type { ElementType } from 'react';
import { useEffect, useState } from 'react';
export interface IPageRendererProps<T> {
  pageData: T;
  isActive: boolean;
}
export type IPageItemFetcher<T> = (id: string) => Promise<null | {
  pageData?: T;
  canonicalResource?: Partial<IResultItem>;
}>;
const getPageFetcherForType = (type: ContentType): IPageItemFetcher<any> => {
  switch (type) {
    case ContentType.insight:
      return insightPageFetcher;
    case ContentType.short:
      return shortsPageFetcher;
    case ContentType.analystreaction:
      return analystReactionsPageFetcher;
    case ContentType.news:
      return newsRsfPageFetcher;
    case ContentType.theme:
      return themePageFetcher;
    default:
      return async () => ({});
  }
};
const EmptyPageRenderer = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

// PageRenderer below is always stable across renders - react/static-components just can't prove that through fn call
// Can't be a lookup map: several of these renderers import this module back (via InfiniteScrollPage), so reading them
// at module-evaluation time hits circular deps and throws "Cannot access 'ShortsPageRenderer' before initialization".
/* eslint-disable react/static-components */
const getPageRendererForType = (type: ContentType): ElementType => {
  switch (type) {
    case ContentType.insight:
      return InsightPageRenderer;
    case ContentType.short:
      return ShortsPageRenderer;
    case ContentType.analystreaction:
      return AnalystReactionsPageRenderer;
    case ContentType.news:
      return NewsRsfRenderer;
    case ContentType.theme:
      return ThemePageRenderer;
    default:
      return EmptyPageRenderer;
  }
};
export const ItemPageRenderer = t0 => {
  const $ = _c(23);
  const {
    id,
    expectedType,
    initialData,
    isActive
  } = t0;
  const [itemId, setItemId] = useState(id);
  const [itemType, setItemType] = useState(expectedType);
  const [pageData, setPageData] = useState(undefined);
  const [errorStatus, setErrorStatus] = useState(null);
  let t1;
  if ($[0] !== itemType) {
    t1 = getPageRendererForType(itemType);
    $[0] = itemType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const PageRenderer = t1;
  const isMounted = useMounted();
  let t2;
  if ($[2] !== expectedType || $[3] !== initialData || $[4] !== isMounted || $[5] !== itemId) {
    t2 = () => {
      if (!initialData) {
        const fetcher = getPageFetcherForType(expectedType);
        fetcher(itemId).then(v => {
          if (isMounted()) {
            if (v === null) {
              setErrorStatus(404);
            } else {
              if (v?.pageData) {
                setPageData(v.pageData);
              } else {
                if (v?.canonicalResource?.type) {
                  setItemType(v.canonicalResource.type as ContentType);
                  setItemId(v.canonicalResource.id);
                } else {
                  setPageData(null);
                }
              }
            }
          }
        }).catch(e => {
          console.warn(`Failed to load content: ${e}`);
          if (isMounted()) {
            setErrorStatus(e?.response?.status || 500);
            setPageData(null);
          }
        });
      }
    };
    $[2] = expectedType;
    $[3] = initialData;
    $[4] = isMounted;
    $[5] = itemId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== itemId || $[8] !== itemType) {
    t3 = [itemId, itemType];
    $[7] = itemId;
    $[8] = itemType;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t2, t3);
  if (initialData) {
    let t4;
    if ($[10] !== PageRenderer || $[11] !== initialData || $[12] !== isActive) {
      t4 = <PageRenderer pageData={initialData} isActive={isActive} />;
      $[10] = PageRenderer;
      $[11] = initialData;
      $[12] = isActive;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  if (errorStatus === 403) {
    let t4;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ForbiddenPage />;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    return t4;
  }
  if (errorStatus === 404) {
    let t4;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <NotFoundErrorPage />;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    return t4;
  }
  if (errorStatus === 451) {
    let t4;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <UnavailableForLegalReasonsPage />;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  if (pageData === undefined) {
    let t4;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Loading />;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    return t4;
  }
  if (pageData === null) {
    let t4;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <></>;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    return t4;
  }
  let t4;
  if ($[19] !== PageRenderer || $[20] !== isActive || $[21] !== pageData) {
    t4 = <PageRenderer pageData={pageData} isActive={isActive} />;
    $[19] = PageRenderer;
    $[20] = isActive;
    $[21] = pageData;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
};